import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  IconButton,
  Typography,
  Box,
  Stack,
  Chip,
  Avatar,
  useTheme,
  alpha,
  Skeleton,
  Pagination,
  Button,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import {
  MdPhone,
  MdLocationOn,
  MdCalendarToday,
  MdVisibility,
  // MdPrescription,
  MdInbox,
} from "react-icons/md";
import axios from "axios";
import dayjs from "dayjs";

const PrescriptionOrder = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();

  useEffect(() => {
    const currentPage = searchParams.get("page") || 1;
    const currentLimit = searchParams.get("limit") || 25;
    setPage(parseInt(currentPage, 10));
    setRowsPerPage(parseInt(currentLimit, 10));
  }, [searchParams]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Note: Make sure your API uses 1-based page numbering
        const response = await axios.get(
          `https://api.medstown.com/customer/dashboard/order/prescription/list?limit=${rowsPerPage}&page=${page}`
        );
        if (response.data.status === 200) {
          setOrders(response.data.data.list);
          setTotalCount(response.data.data.totalCount);
          // Calculate total pages
          setTotalPages(Math.ceil(response.data.data.totalCount / rowsPerPage));
        }
      } catch (error) {
        console.error("Error fetching prescription orders:", error);
        setOrders([]);
        setTotalCount(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setSearchParams({ page: newPage, limit: rowsPerPage });
  };

  const handleCall = (phoneNumber) => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleViewOrder = (orderId) => {
    window.open(
      `https://teamdb.medstown.com/admin/order/rx/${orderId}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  if (loading && !orders.length) {
    return (
      <Card sx={{ 
        mt: 4, 
        borderRadius: 3, 
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        p: 3
      }}>
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 2 }} />
          {Array.from(new Array(5)).map((_, index) => (
            <Skeleton key={index} variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
          ))}
        </Stack>
      </Card>
    );
  }

  return (
    <Card sx={{ 
      mt: 4, 
      borderRadius: 3, 
      boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
      border: '1px solid',
      borderColor: alpha(theme.palette.divider, 0.1),
      overflow: 'hidden'
    }}>
      {/* Loading Progress Bar */}
      {loading && (
        <LinearProgress 
          sx={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1,
            height: 3,
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #9c27b0, #e91e63, #9c27b0)'
            }
          }} 
        />
      )}

      {/* Header */}
      <Box sx={{ p: 3, pb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" fontWeight={700} color="secondary.main" sx={{ mb: 0.5 }}>
              {/* <MdPrescription size={24} style={{ marginRight: 8, verticalAlign: 'middle' }} /> */}
              Prescription Orders
            </Typography>
            {/* <Typography variant="body2" color="text.secondary">
              {totalCount || 0} total prescription orders • Page {page} of {totalPages}
            </Typography> */}
          </Box>

          {/* Quick Stats */}
          <Stack direction="row" spacing={2}>
            <Chip 
              label={`${totalCount} Total`}
              variant="outlined"
              sx={{ 
                borderColor: alpha(theme.palette.secondary.main, 0.3),
                color: theme.palette.secondary.main,
                fontWeight: 600
              }}
            />
          </Stack>
        </Stack>
      </Box>

      <TableContainer sx={{ px: 3 }}>
        <Table size="small" sx={{ '& .MuiTableCell-root': { py: 2 } }}>
          <TableHead>
            <TableRow sx={{ '& .MuiTableCell-head': { 
              backgroundColor: alpha(theme.palette.secondary.main, 0.06),
              fontWeight: 700,
              fontSize: '0.8rem',
              color: theme.palette.secondary.main,
              border: 'none',
              py: 2,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}}>
              <TableCell>Order ID</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && orders.length === 0 ? (
              Array.from(new Array(6)).map((_, index) => (
                <TableRow key={index}>
                  {[...Array(6)].map((_, i) => (
                    <TableCell key={i}>
                      <Skeleton 
                        variant="rectangular" 
                        height={28} 
                        sx={{ borderRadius: 1.5, bgcolor: alpha(theme.palette.secondary.main, 0.05) }} 
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : orders?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                  <Stack direction="column" alignItems="center" spacing={3}>
                    <Avatar sx={{ 
                      bgcolor: alpha(theme.palette.secondary.main, 0.1),
                      width: 72,
                      height: 72
                    }}>
                      <MdInbox size={36} color={theme.palette.secondary.main} />
                    </Avatar>
                    <Box textAlign="center">
                      <Typography variant="h6" color="text.primary" fontWeight={600} sx={{ mb: 1 }}>
                        No prescription orders found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Prescription orders will appear here when available
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => (
                <TableRow 
                  key={order._id}
                  sx={{ 
                    '&:hover': { 
                      backgroundColor: alpha(theme.palette.secondary.main, 0.03),
                      transform: 'translateY(-1px)',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.1)}`
                    },
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                    transition: 'all 0.2s ease'
                  }}
                >
                  {/* Order ID */}
                  <TableCell sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                    <Box component="span" sx={{ 
                      color: theme.palette.secondary.main,
                      fontFamily: 'monospace',
                      backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem'
                    }}>
                      {order.orderId}
                    </Box>
                  </TableCell>
                  
                  {/* Customer Info */}
                  <TableCell sx={{ fontSize: '0.8125rem' }}>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8125rem' }}>
                        {order.customerName}
                      </Typography>
                      {/* <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        ID: {order.orderId}
                      </Typography> */}
                    </Stack>
                  </TableCell>
                  
                  {/* Contact */}
                  <TableCell sx={{ fontSize: '0.8125rem' }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                        {order.customerPhone}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleCall(order.customerPhone)}
                        sx={{
                          color: theme.palette.success.main,
                          backgroundColor: alpha(theme.palette.success.main, 0.1),
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.success.main, 0.2),
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <MdPhone size={14} />
                      </IconButton>
                    </Stack>
                  </TableCell>
                  
                  {/* Location */}
                  <TableCell sx={{ fontSize: '0.8125rem' }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <MdLocationOn size={14} color={theme.palette.text.secondary} />
                      <Chip 
                        label={order.customer_address?.postalCode || 'N/A'}
                        size="small"
                        sx={{ 
                          fontSize: '0.7rem',
                          height: 22,
                          backgroundColor: alpha(theme.palette.info.main, 0.1),
                          color: theme.palette.info.main,
                          border: 'none',
                          fontWeight: 600
                        }}
                      />
                    </Stack>
                  </TableCell>
                  
                  {/* Created Date */}
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <MdCalendarToday size={14} />
                      <Box>
                        <Box sx={{ fontWeight: 500 }}>
                          {dayjs(order.createdAt).format("MMM DD, YYYY")}
                        </Box>
                        <Box sx={{ fontSize: '0.7rem', opacity: 0.8 }}>
                          {dayjs(order.createdAt).format("hh:mm A")}
                        </Box>
                      </Box>
                    </Stack>
                  </TableCell>
                  
                  {/* Actions */}
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<MdVisibility size={14} />}
                      onClick={() => handleViewOrder(order.orderId)}
                      sx={{
                        backgroundColor: theme.palette.secondary.main,
                        color: 'white',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        px: 2,
                        py: 0.8,
                        borderRadius: 2,
                        textTransform: 'none',
                        boxShadow: `0 2px 8px ${alpha(theme.palette.secondary.main, 0.3)}`,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.secondary.main, 0.8),
                          transform: 'translateY(-1px)',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.4)}`
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      View Order
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Enhanced Pagination - Same as previous conversation */}
      <Box sx={{ p: 3, pt: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {/* Results Info */}
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            Showing {orders?.length || 0} of {totalCount || 0} prescription orders
          </Typography>

          {/* Pagination - Using Material-UI Pagination component */}
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={page}
              onChange={handleChangePage}
              size="medium"
              variant="outlined"
              shape="rounded"
              sx={{
                '& .MuiPaginationItem-root': {
                  fontSize: '0.875rem',
                  minWidth: 36,
                  height: 36,
                  margin: '0 2px',
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.secondary.main,
                    color: 'white',
                    fontWeight: 600,
                    border: `1px solid ${theme.palette.secondary.main}`,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.secondary.main, 0.8),
                    }
                  },
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                    borderColor: alpha(theme.palette.secondary.main, 0.4),
                  }
                }
              }}
            />
          )}
        </Stack>
      </Box>
    </Card>
  );
};

export default PrescriptionOrder;
